import axios from "axios";
import __ from "lodash";
import DetectRTC from "detectrtc";
import { CALL_DATA, PublicVariables } from "../constants/PublicVariables";
import { APIs } from "../constants/APIConstants";
import {
    BASEURL,
    CLIENTAPPID,
    DEVICETYPE,
    EXTRACT_TEXT_VIEW,
    INVALID_DOMAIN_URL,
    LOCAL_STORAGE_KEYS,
    MARKUP_VIEW,
    ANALYTICSBASEURL,
    REQUEST_TIMEOUT,
    STORAGE_URL,
} from "../constants/Constants";
import CustomToast from "../components/UI/customToast";
import storage from "../services/storage";
import OnLoadFunctionAPI from "./onLoadFunctionApi";
import SurveyAPI from "./surveyAPI";
import { getExactBrowserName } from "../shared/utility";
import JoinMeetingAPIFunction from "./joinMeetingApi";
import { DefaultLanguage } from "../constants/LanguageConstants";
import NotesAPIFunction from "./notesApi";
import EndCallAPIFunction from "./endCallApi";
import BookmarkAPI from "./bookmarkApi";
import { LogFirebase } from "../services/firestore";
import RecordingAPI from "./recordingApi";
import reduxStore from "../configureEnvStore";
import LiveCaptionAPI from "./liveCaptionApi";

const bearerKey = "Bearer ";
const contentTypeKey = "Content-Type";
const API_STATUS = {
    SUCCESS: 200,
    CREATED: 201,
    NO_CONTENT: 204,
    NOT_FOUND: 404,
    UNAUTHORIZED: 401,
    INTERNAL_SERVER: 500,
    ERR_NETWORK: 0,
};
const errorFunction = (error) => {
    if (error.response) {
        const statusCode = error.response?.status;
        if (statusCode === API_STATUS.NOT_FOUND && error?.config?.url && error.config.url.includes(APIs.ISCOMPANYAVAILABLE)) {
            if (PublicVariables?.companyPortalName) {
                /* If localization is needed, then set the error message into the localized version */
                CustomToast.error("domain_contact_admin_msg");
            } else {
                /* For SSO Invalid domain */
                window.location.href = INVALID_DOMAIN_URL;
            }
        } else if (statusCode === API_STATUS.INTERNAL_SERVER) {
            CustomToast.error(error.response?.data?.Message);
        }
        // else if (statusCode === API_STATUS.UNAUTHORIZED) {
        //     PublicVariables.statusCode = error.response?.data?.error?.code;
        //     PublicVariables.message = error.response?.data?.error?.message;
        //     if (error.response?.data?.error?.title) {
        //         PublicVariables.title = error.response?.data?.error?.title;
        //         /* Write code below to display the status error modal */
        //     }
        //     else {
        //         CustomToast.error(error.response?.data?.error?.message)
        //     }
        // }
        else if (statusCode === API_STATUS.ERR_NETWORK) {
            CustomToast.error(error?.message);
        }
    }
};

const getLocalBrowserInfoForHeader = (header) => {
    /* Set ldFlags when logging in, and after the login process, we need to set idFlags.*/
    if (PublicVariables.ldFlags?.captureDeviceAndGoogleEvent) {
        const browserInfo = getExactBrowserName();
        header.BROWSER = browserInfo?.browserName;
        header.BROWSERVERSION = browserInfo?.browserVersion;
        header.OS = DetectRTC.osName;
        header.Ulat = CALL_DATA.Latitude;
        header.Ulong = CALL_DATA.Longitude;
        header.LATITUDE = CALL_DATA.Latitude;
        header.LONGITUDE = CALL_DATA.Longitude;
    }
    header.DEVICETYPE = DEVICETYPE;
    header.clientappid = CLIENTAPPID;
    const currentlanguage = PublicVariables.currentLanguageObj?.languageCode ?? DefaultLanguage;
    header["Accept-Language"] = currentlanguage.replace(/_/g, "-");
};

class API {
    __onloadfunction = new OnLoadFunctionAPI();
    __joinMeetingFunction = new JoinMeetingAPIFunction();
    __survey = new SurveyAPI();
    __notes = new NotesAPIFunction();
    __endCallAPIFunction = new EndCallAPIFunction();
    __bookmarkAPI = new BookmarkAPI();
    __recordingAPI = new RecordingAPI();
    __liveCaptionAPI = new LiveCaptionAPI();

    lat = 0;
    long = 0;

    API() {
        /* call this function after LD initialized */
        this.getCoordinates();
    }

    get onloadfunction() {
        return this.__onloadfunction;
    }

    get joinMeetingFunction() {
        return this.__joinMeetingFunction;
    }
    get survey() {
        return this.__survey;
    }
    get notes() {
        return this.__notes;
    }

    get endCallAPIFunction() {
        return this.__endCallAPIFunction;
    }

    get bookmark() {
        return this.__bookmarkAPI;
    }

    get recording() {
        return this.__recordingAPI;
    }

    get liveCaption() {
        return this.__liveCaptionAPI;
    }

    api = axios.create({
        baseURL: BASEURL + APIs.API,
        transformRequest: [(data) => JSON.stringify(data)],
        headers: {
            Accept: "application/json",
            clientappid: CLIENTAPPID,
            [contentTypeKey]: "application/json",
        },
    });

    formsApi = axios.create({
        baseURL: BASEURL + APIs.API,
        headers: {
            Accept: "*",
            clientappid: CLIENTAPPID,
            [contentTypeKey]: undefined,
            UserToken: "",
        },
    });

    storageApi = axios.create({
        baseURL: STORAGE_URL,
        headers: {
            Accept: "application/json",
            clientappid: CLIENTAPPID,
            [contentTypeKey]: "application/json",
        },
    });

    analyticsApi = axios.create({
        baseURL: ANALYTICSBASEURL,
        headers: {
            Accept: "application/json",
            clientappid: CLIENTAPPID,
            [contentTypeKey]: "application/json",
        },
    });

    storageApiWithoutHeader = axios.create({
        baseURL: STORAGE_URL,
    });

    getCoordinates() {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    this.lat = position.coords.latitude;
                    this.long = position.coords.longitude;
                },
                (_error) => {
                    this.lat = this.long = 0;
                }
            );
        } else {
            this.lat = this.long = 0;
        }
    }

    get(url, ...args) {
        return this.sendRequestInternal(this.api.get, url, ...args);
    }

    post(url, ...args) {
        return this.sendRequestInternal(this.api.post, url, ...args);
    }

    postWithAnalyticsAPI(url, ...args) {
        return this.sendRequestInternal(this.analyticsApi.post, url, ...args);
    }

    put(url, ...args) {
        return this.sendRequestInternal(this.api.put, url, ...args);
    }

    delete(url, ...args) {
        return this.sendRequestInternal(this.api.delete, url, ...args);
    }

    postForm(url, ...args) {
        return this.sendRequestInternalForm(this.formsApi.post, url, ...args);
    }

    postStorage(url, ...args) {
        return this.sendStorageRequestInternal(this.storageApi.post, url, ...args);
    }

    deleteStorage(url, ...args) {
        return this.sendStorageRequestInternal(this.storageApi.delete, url, ...args);
    }

    postStorageWithoutHeader(url, ...args) {
        return this.sendRequestInternal(this.storageApiWithoutHeader.post, url, ...args);
    }

    commonCatchErrorFunction(error) {
        console.log(error);
        let errorVal = {};
        /* Handle errors */
        if (error?.code === "ERR_NETWORK") {
            errorFunction(error);
            errorVal = {
                message: error.message,
            };
        } else if (error.response?.data) {
            // The request was made and the server responded with a status code
            console.error("Axios response error:", error.response.status);
            errorFunction(error);
            errorVal = error.response?.data?.error;
        } else if (error.request) {
            // The request was made but no response was received
            console.error("Axios request error:", error.request);
            errorFunction(error);
            errorVal = {
                code: error.code,
                message: error.message,
                status: error.request?.status,
            };
        } else {
            // Something happened in setting up the request that triggered an error
            console.error("Axios setup error:", error.message);
            errorVal = {
                code: error.code,
                message: error.message,
                status: error?.request?.status || error?.status,
            };
        }
        return errorVal;
    }

    commonSuccessFunctionFinalResponse = (response) => {
        return response.data;
    };

    commonSuccessFunction = (response) => {
        if (response.status === API_STATUS.SUCCESS || response.status === API_STATUS.CREATED) {
            return this.commonSuccessFunctionFinalResponse(response);
        } else if (response.status === API_STATUS.NO_CONTENT) {
            return true;
        } else {
            return false;
        }
    };

    commonRequestFunction = async (requestFunc, url, ...args) => {
        return requestFunc(url, ...args)
            .then((response) => {
                return this.commonSuccessFunction(response);
            })
            .catch((error) => {
                throw this.commonCatchErrorFunction(error);
            });
    };

    sendRequestInternal(requestFunc, url, ...args) {
        if (args.length > 0) {
            if (args[0].hasOwnProperty("headers")) {
                Object.keys(args[0].headers).forEach((key) => {
                    this.api.defaults.headers[key] = args[0].headers[key];
                });
                console.log(this.api.defaults.headers);
                args = [__.omit(args[0], ["headers"])];
            }
        }

        if (CALL_DATA.memberToken) {
            this.api.defaults.headers["Authorization"] = bearerKey + CALL_DATA.memberToken;
        }

        getLocalBrowserInfoForHeader(this.api.defaults.headers);
        return this.commonRequestFunction(requestFunc, url, ...args);
    }

    sendRequestInternalForm(requestFunc, url, ...args) {
        const userData = storage.get(LOCAL_STORAGE_KEYS.USER_DATA);
        if (userData?.userToken) {
            this.formsApi.defaults.headers["Authorization"] = bearerKey + storage.get(LOCAL_STORAGE_KEYS.USER_TOKEN);
        }

        return requestFunc(url, ...args)
            .then((response) => {
                return response.data;
            })
            .catch((error) => {
                errorFunction(error);
                throw error;
            });
    }

    sendStorageRequestInternal(requestFunc, url, ...args) {
        if (args.length > 0) {
            if (args[0].hasOwnProperty("headers")) {
                Object.keys(args[0].headers).forEach((key) => {
                    this.storageApi.defaults.headers[key] = args[0].headers[key];
                });
                args = [__.omit(args[0], ["headers"])];
            }
        }

        getLocalBrowserInfoForHeader(this.storageApi.defaults.headers);
        if (requestFunc === this.storageApi.delete) {
            args = [{ data: args[0] }];
        }
        return this.commonRequestFunction(requestFunc, url, ...args);
    }

    setRequestHeaderForImageAPI = (xhr, header) => {
        for (let headerName in header) {
            xhr.setRequestHeader(headerName, header[headerName]);
        }
    };

    setupAbortButtonForImageAPI = (xhr) => {
        if (
            reduxStore.getState().extractText?.extractTextInprogress === EXTRACT_TEXT_VIEW.OCR_VIEW ||
            reduxStore.getState().markup.markupInprogress === MARKUP_VIEW.START_MARKUP_VIEW
        ) {
            // code for OCR MArk up
            let el = document.createElement("button");
            el.style.position = "absolute";
            el.style.left = "-9999px";
            el.setAttribute("id", "abortImageRequest");
            document.body.appendChild(el);
            document.addEventListener("click", function (e) {
                if (e.target && e.target.id === "abortImageRequest") {
                    xhr.abort();
                }
            });
        }
    };

    handleSuccessResponseForImageAPI = (response, resolve) => {
        if (response.success === 0) {
            LogFirebase("APIDetails", "API_Error_sendImageRequest", { status: response.data.errorcode, statusText: response.data.error });
        } else if (response.success < 0) {
            LogFirebase("APIDetails", "API_Error_success_less_0", { response: response.success, status: response.data.errorcode });
        }
        resolve(response);
    };

    handleRequestErrorForImageAPI = (xhr, errData, errorCode, errorMessage, reject) => {
        errData.data = { errorcode: errorCode, error: errorMessage };
        LogFirebase("APIDetails", "API_Failed_sendImageRequest", { url: xhr.responseURL, status: xhr.status, statusText: xhr.statusText });
        const el2 = document.getElementById("abortImageRequest"); // this code for OCR MArk up
        console.error(el2);
        if (el2) {
            document.body.removeChild(el2);
        }
        reject(errData);
    };

    handleErrorResponseForImageAPI = (response, errData, reject) => {
        if (response?.data?.error) {
            LogFirebase("APIDetails", "API_Error_sendImageRequest", { status: response.data.errorcode, statusText: response.data.error });
        } else if (response?.errors?.length > 0) {
            LogFirebase("APIDetails", "API_Error_sendImageRequest_found_Error", { status: response.status, statusText: response.errors[0] });
        } else if (response?.status) {
            LogFirebase("APIDetails", "API_Error_sendImageRequest_found_Status", { status: response.status });
        } else {
            LogFirebase("APIDetails", "API_Error_sendImageRequest", { status: "", statusText: "" });
        }
        reject(errData);
    };

    sendImageRequest(url, requestdata, header = {}) {
        getLocalBrowserInfoForHeader(header);
        return new Promise((resolve, reject) => {
            let xhr = new XMLHttpRequest();
            let errData = { success: 0, data: {} };
            let errMsg = "Error occurred in processing the request. Please try again.";

            xhr.onload = () => {
                if (xhr.readyState === 4) {
                    let response = JSON.parse(xhr.responseText);
                    if (xhr.status >= 200 && xhr.status < 300) {
                        this.handleSuccessResponseForImageAPI(response, resolve);
                    } else {
                        this.handleErrorResponseForImageAPI(response, errData, reject);
                    }
                }
            };

            xhr.onerror = () => this.handleRequestErrorForImageAPI(xhr, errData, "-222", "Upload failed", reject);
            xhr.onabort = () => this.handleRequestErrorForImageAPI(xhr, errData, "-111", errMsg, reject);
            xhr.ontimeout = () => this.handleRequestErrorForImageAPI(xhr, errData, "-111", errMsg, reject);
            this.setupAbortButtonForImageAPI(xhr);
            xhr.open("POST", STORAGE_URL + url, true);
            xhr.timeout = REQUEST_TIMEOUT;
            this.setRequestHeaderForImageAPI(xhr, header);
            xhr.send(requestdata);
        });
    }
}

export default new API();
